// frontend/src/components/CreateOrganization.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Building2, Upload, CheckCircle, XCircle, ShieldCheck, Building, CreditCard } from 'lucide-react';
import * as GST_STATES from 'gst-states';

const STANDARD_SIZE = 300;

// Normalize gst-states export shape and keep only valid code->name pairs.
const GST_STATES_MAP = GST_STATES?.default && typeof GST_STATES.default === 'object'
  ? GST_STATES.default
  : GST_STATES;

const stateOptions = Object.entries(GST_STATES_MAP)
  .filter(([code, name]) => /^\d{2}$/.test(String(code)) && typeof name === 'string')
  .map(([code, name]) => ({
    code,
    name,
    label: `${name} (${code})`
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const CreateOrganization = ({ userId, tenantId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [organizationDetails, setOrganizationDetails] = useState({
    organizationName: '',
    address: '',
    phone: '',
    email: '',
  });

  const [billingDetails, setBillingDetails] = useState({
    billingName: '',
    billingAddress: '',
    city: '',
    state: '',
    stateCode: '',
    country: 'India',
    pincode: '',
    gstin: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoError, setLogoError] = useState('');

  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = STANDARD_SIZE;
          canvas.height = STANDARD_SIZE;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, STANDARD_SIZE, STANDARD_SIZE);
          canvas.toBlob(
            (blob) => {
              resolve(blob);
            },
            'image/jpeg',
            0.8
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    setLogoError('');
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setLogoError('Logo must be a JPEG or PNG image.');
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      setLogoError('Logo must be less than 1MB.');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    
    // Clear errors for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-populate state code when state is selected
    if (name === 'state') {
      const selectedState = stateOptions.find(s => s.name === value);
      setBillingDetails(prev => ({
        ...prev,
        state: value,
        stateCode: selectedState ? selectedState.code : ''
      }));
      return;
    }

    // Auto-populate state name when state code is entered
    if (name === 'stateCode') {
      const selectedState = stateOptions.find(s => s.code === value);
      setBillingDetails(prev => ({
        ...prev,
        stateCode: value,
        state: selectedState ? selectedState.name : ''
      }));
      return;
    }

    setBillingDetails(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Organization validation
    if (!organizationDetails.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
      isValid = false;
    }

    if (!organizationDetails.address.trim()) {
      newErrors.address = 'Address is required';
      isValid = false;
    }

    if (!organizationDetails.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    } else {
      const fullNumber = organizationDetails.phone.startsWith('+') 
        ? organizationDetails.phone 
        : `+${organizationDetails.phone}`;
      const phoneNumber = parsePhoneNumberFromString(fullNumber);
      if (!phoneNumber || !phoneNumber.isValid()) {
        newErrors.phone = 'Invalid global phone number format';
        isValid = false;
      }
    }

    if (!organizationDetails.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(organizationDetails.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    if (!logoFile) {
      setLogoError('Logo is required');
      isValid = false;
    }

    // Billing validation - simple checks
    const hasBillingData = Object.values(billingDetails).some(val => val && val.trim().length > 0);
    
    if (hasBillingData) {
      // If GSTIN is provided, check length
      if (billingDetails.gstin && billingDetails.gstin.trim()) {
        const gstin = billingDetails.gstin.trim().replace(/\s/g, '');
        if (gstin.length !== 15) {
          newErrors.gstin = 'GSTIN must be 15 characters';
          isValid = false;
        }
      }

      // Validate pincode if provided
      if (billingDetails.pincode && billingDetails.pincode.trim()) {
        if (!/^[0-9]{6}$/.test(billingDetails.pincode.trim())) {
          newErrors.pincode = 'Pincode must be 6 digits';
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setLogoError('');
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    let uploadedLogoFileId = null;
    let uploadedLogoUrl = null;
    
    try {
      const currentTenantId = tenantId || location.state?.tenantId;
      
      if (!currentTenantId) {
        throw new Error('Tenant ID is missing. Please refresh and try again.');
      }
      
      // Upload logo
      if (logoFile) {
        const processedBlob = await processImage(logoFile);
        const formData = new FormData();
        formData.append('file', processedBlob, logoFile.name.replace(/\.[^/.]+$/, ".jpg"));
        
        const token = localStorage.getItem('token');
        const uploadRes = await api.post(`/api/organization/${currentTenantId}/logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });
        
        uploadedLogoFileId = uploadRes.data.fileId;
        uploadedLogoUrl = uploadRes.data.downloadUrl || uploadRes.data.fileUrl;
      }

      // Prepare billing details - only send if there's data
      const hasBillingData = Object.values(billingDetails).some(val => val && val.trim().length > 0);
      const billingPayload = hasBillingData ? {
        billingName: billingDetails.billingName || null,
        billingAddress: billingDetails.billingAddress || null,
        city: billingDetails.city || null,
        state: billingDetails.state || null,
        stateCode: billingDetails.stateCode || null,
        country: billingDetails.country || 'India',
        pincode: billingDetails.pincode || null,
        gstin: billingDetails.gstin ? billingDetails.gstin.trim().toUpperCase() : null,
      } : null;

      const token = localStorage.getItem('token');
      const payload = {
        ...organizationDetails,
        tenantId: currentTenantId,
        logoFileId: uploadedLogoFileId,
        logoUrl: uploadedLogoUrl,
        billingDetails: billingPayload
      };
      
      const response = await api.post(`/api/organization/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubmitStatus({
        success: true,
        message: response.data.message || 'Organization created successfully!'
      });

      setTimeout(() => {
        navigate(`/tenant-admin/admin`, {
          state: {
            ...location.state,
            userId: userId || location.state?.userId,
            tenantId: currentTenantId,
          },
        });
      }, 2000);
      
    } catch (err) {
      console.error('[CreateOrganization] Error:', err);
      setSubmitStatus({
        success: false,
        message: err.response?.data?.message || err.message || 'Error creating organization.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 md:py-12 flex flex-col items-center justify-center">
      <div className="max-w-5xl w-full">
        
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="mx-auto h-14 w-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-md shadow-blue-600/10 mb-4">
            <Building2 className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Create Your Organization
          </h1>
          <p className="mt-2 text-sm md:text-base text-slate-500 max-w-md mx-auto leading-relaxed">
            Set up your educational institution's corporate profile and billing information.
          </p>
        </div>

        {/* Status Alert */}
        {submitStatus.message && (
          <div className={`mb-6 p-4 rounded-xl flex gap-3 max-w-4xl mx-auto ${
            submitStatus.success 
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}>
            {submitStatus.success ? (
              <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
            )}
            <p className="text-xs font-semibold leading-relaxed">{submitStatus.message}</p>
          </div>
        )}

        {/* Main Form */}
        <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
          <form onSubmit={handleCreateOrganization}>
            <div className="flex flex-col lg:flex-row">
              
              {/* Left Panel - Logo */}
              <div className="w-full lg:w-4/12 bg-slate-50/80 p-6 md:p-8 flex flex-col items-center justify-center border-b border-slate-200 lg:border-b-0 lg:border-r border-slate-100">
                <div className="text-center mb-6">
                  <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Institution Logo</h2>
                  <p className="text-xs text-slate-400 mt-1">Upload a clear square branding graphic.</p>
                </div>

                <div className="w-full max-w-[200px]">
                  <div className="relative group aspect-square">
                    <div className={`flex flex-col items-center justify-center border-2 w-full h-full rounded-2xl p-4 bg-white transition-all text-center relative ${
                      logoError ? 'border-rose-300 bg-rose-50/10' : 'border-dashed border-slate-200 group-hover:border-blue-400'
                    }`}>
                      <input
                        id="logo-upload"
                        name="logo"
                        type="file"
                        accept="image/png, image/jpeg"
                        onChange={handleLogoChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      
                      {logoPreview ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img 
                            src={logoPreview} 
                            alt="Logo Preview" 
                            className="max-h-full max-w-full object-contain rounded-xl"
                          />
                          <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-xl transition-all">
                            <span className="text-white text-xs font-semibold px-2.5 py-1.5 bg-slate-900/80 rounded-lg">Change</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center p-2 select-none">
                          <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl mb-2">
                            <Upload className="w-5 h-5" />
                          </div>
                          <span className="text-xs text-slate-600 font-semibold block">Click to upload</span>
                          <span className="text-[10px] text-slate-400 mt-0.5 block">PNG or JPG under 1MB</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {logoError && (
                    <p className="mt-2.5 text-xs text-rose-600 font-medium text-center flex items-center justify-center gap-1">
                      <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {logoError}
                    </p>
                  )}
                </div>
              </div>

              {/* Right Panel - Forms */}
              <div className="w-full lg:w-8/12 p-5 md:p-8">
                
                {/* Organization Details */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                    <Building className="w-4 h-4 text-blue-600" />
                    Organization Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="organizationName" className="block text-xs font-semibold text-slate-700 mb-1">
                        Organization Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="organizationName"
                        name="organizationName"
                        type="text"
                        value={organizationDetails.organizationName}
                        onChange={(e) => {
                          setOrganizationDetails(prev => ({ ...prev, organizationName: e.target.value }));
                          if (errors.organizationName) {
                            setErrors(prev => ({ ...prev, organizationName: '' }));
                          }
                        }}
                        className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                          errors.organizationName ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                        }`}
                        placeholder="e.g. Harvard University"
                      />
                      {errors.organizationName && (
                        <p className="mt-1 text-xs text-rose-600 font-medium">{errors.organizationName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-xs font-semibold text-slate-700 mb-1">
                        Address <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        id="address"
                        name="address"
                        rows={2}
                        value={organizationDetails.address}
                        onChange={(e) => {
                          setOrganizationDetails(prev => ({ ...prev, address: e.target.value }));
                          if (errors.address) {
                            setErrors(prev => ({ ...prev, address: '' }));
                          }
                        }}
                        className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all resize-none ${
                          errors.address ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                        }`}
                        placeholder="123 Education St, Cambridge, MA"
                      />
                      {errors.address && (
                        <p className="mt-1 text-xs text-rose-600 font-medium">{errors.address}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 mb-1">
                          Phone Number <span className="text-rose-500">*</span>
                        </label>
                        <PhoneInput
                          country={'us'}
                          value={organizationDetails.phone}
                          onChange={(value) => {
                            const formattedValue = value.startsWith('+') ? value : `+${value}`;
                            setOrganizationDetails(prev => ({ ...prev, phone: formattedValue }));
                            if (errors.phone) {
                              setErrors(prev => ({ ...prev, phone: '' }));
                            }
                          }}
                          placeholder="+1 (555) 000-0000"
                          inputProps={{ id: 'phone', name: 'phone', required: true }}
                          containerClass="w-full"
                          inputClass={`!w-full !text-sm !h-[40px] !border !rounded-lg focus:!outline-none focus:!ring-2 focus:!ring-blue-500/20 !transition-all ${
                            errors.phone ? '!border-rose-300 !bg-rose-50/10 focus:!border-rose-500' : '!border-slate-200 focus:!border-blue-500'
                          }`}
                          buttonClass="!border !border-slate-200 !rounded-l-lg !bg-slate-50"
                        />
                        {errors.phone && (
                          <p className="mt-1 text-xs text-rose-600 font-medium">{errors.phone}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
                          Email <span className="text-rose-500">*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={organizationDetails.email}
                          onChange={(e) => {
                            setOrganizationDetails(prev => ({ ...prev, email: e.target.value }));
                            if (errors.email) {
                              setErrors(prev => ({ ...prev, email: '' }));
                            }
                          }}
                          className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.email ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="admin@institution.edu"
                        />
                        {errors.email && (
                          <p className="mt-1 text-xs text-rose-600 font-medium">{errors.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing Details - Optional Section */}
                <div className="border-t border-slate-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                      Billing Information
                      <span className="text-xs font-normal text-slate-400">(Optional)</span>
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="billingName" className="block text-xs font-semibold text-slate-700 mb-1">
                          Billing Name
                        </label>
                        <input
                          id="billingName"
                          name="billingName"
                          type="text"
                          value={billingDetails.billingName}
                          onChange={handleBillingChange}
                          className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.billingName ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="Legal business name"
                        />
                        {errors.billingName && (
                          <p className="mt-1 text-xs text-rose-600 font-medium">{errors.billingName}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="gstin" className="block text-xs font-semibold text-slate-700 mb-1">
                          GSTIN
                          <span className="text-xs font-normal text-slate-400 ml-1">(Optional)</span>
                        </label>
                        <input
                          id="gstin"
                          name="gstin"
                          type="text"
                          value={billingDetails.gstin}
                          onChange={handleBillingChange}
                          className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all uppercase ${
                            errors.gstin ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="27AABCG1234D1Z5"
                          maxLength="15"
                        />
                        {errors.gstin && (
                          <p className="mt-1 text-xs text-rose-600 font-medium">{errors.gstin}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="billingAddress" className="block text-xs font-semibold text-slate-700 mb-1">
                        Billing Address
                      </label>
                      <textarea
                        id="billingAddress"
                        name="billingAddress"
                        rows={2}
                        value={billingDetails.billingAddress}
                        onChange={handleBillingChange}
                        className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                        placeholder="Registered office address"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="city" className="block text-xs font-semibold text-slate-700 mb-1">
                          City
                        </label>
                        <input
                          id="city"
                          name="city"
                          type="text"
                          value={billingDetails.city}
                          onChange={handleBillingChange}
                          className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                          placeholder="Mumbai"
                        />
                      </div>

                      <div>
                        <label htmlFor="state" className="block text-xs font-semibold text-slate-700 mb-1">
                          State
                        </label>
                        <select
                          id="state"
                          name="state"
                          value={billingDetails.state}
                          onChange={handleBillingChange}
                          className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all bg-white"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map((state) => (
                            <option key={state.code} value={state.name}>
                              {state.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="stateCode" className="block text-xs font-semibold text-slate-700 mb-1">
                          State Code
                          <span className="text-xs font-normal text-slate-400 ml-1">(Auto)</span>
                        </label>
                        <input
                          id="stateCode"
                          name="stateCode"
                          type="text"
                          value={billingDetails.stateCode}
                          onChange={handleBillingChange}
                          className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all bg-slate-50"
                          placeholder="27"
                          maxLength="2"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Auto-filled when state is selected</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="pincode" className="block text-xs font-semibold text-slate-700 mb-1">
                          Pincode
                        </label>
                        <input
                          id="pincode"
                          name="pincode"
                          type="text"
                          value={billingDetails.pincode}
                          onChange={handleBillingChange}
                          className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.pincode ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="400001"
                          maxLength="6"
                        />
                        {errors.pincode && (
                          <p className="mt-1 text-xs text-rose-600 font-medium">{errors.pincode}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="country" className="block text-xs font-semibold text-slate-700 mb-1">
                          Country
                        </label>
                        <input
                          id="country"
                          name="country"
                          type="text"
                          value={billingDetails.country}
                          onChange={handleBillingChange}
                          className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                          placeholder="India"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-slate-200 mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center items-center gap-2 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating Organization...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4" />
                        Create Organization
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;